import { datadogRum } from "@datadog/browser-rum";

import type { QuoteWorkflow } from "./google-analytics-helper.ts";
import { shouldLogGaEvents } from "./google-analytics-helper.ts";

export type DdWorkflow = "Create Quote" | "Resume Quote";

/** Converts GA workflow key to DataDog-style display name. */
export function toDdWorkflow(workflow: QuoteWorkflow): DdWorkflow {
	return workflow === "create_quote" ? "Create Quote" : "Resume Quote";
}

/** Wrapper that logs DD actions to console in non-prod environments and sends to DataDog. */
function logAndAddDdAction(
	name: string,
	context: Record<string, unknown>,
): void {
	if (shouldLogGaEvents()) {
		console.log("[DD Action]", name, context);
	}
	datadogRum.addAction(name, context);
}

/**
 * Sends a DataDog RUM action when the user creates a quote (Step 1).
 */
export function ddCreateQuote(quoteId: string, workflow: DdWorkflow): void {
	logAndAddDdAction("Create Quote", {
		workflow,
		quote_id: quoteId,
	});
}

/**
 * Sends a DataDog RUM action when the user books a carrier (Step 2).
 */
export function ddBookCarrier(
	quoteId: string,
	carrierId: string,
	workflow: DdWorkflow,
): void {
	logAndAddDdAction("Book Carrier", {
		workflow,
		quote_id: quoteId,
		carrier_id: carrierId,
	});
}

/**
 * Sends a DataDog RUM action when the user tenders a quote (Step 3).
 */
export function ddTenderQuote(
	quoteId: string,
	rateAdjustment: string,
	workflow: DdWorkflow,
): void {
	logAndAddDdAction("Tender Quote", {
		workflow,
		quote_id: quoteId,
		rate_adjustment: rateAdjustment,
	});
}

/**
 * Sends a DataDog RUM action when the user saves a quote for later.
 */
export function ddSaveQuote(quoteId: string, workflow: DdWorkflow): void {
	logAndAddDdAction("Save Quote", {
		workflow,
		quote_id: quoteId,
	});
}

/**
 * Sends a DataDog RUM action when the user selects an address book entry.
 */
export function ddUseAddressBook(
	workflow: DdWorkflow,
	section: "pickup" | "drop",
): void {
	logAndAddDdAction("Use Address Book", {
		workflow,
		feature: "address_book",
		section,
	});
}

/**
 * Sends a DataDog RUM action when the user selects a favorited address.
 */
export function ddUseFavAddress(
	workflow: DdWorkflow,
	section: "pickup" | "drop",
): void {
	logAndAddDdAction("Use Fav Address", {
		workflow,
		feature: "address_book",
		section,
	});
}

/**
 * Sends a DataDog RUM action when the user selects a default address.
 */
export function ddUseDefAddress(
	workflow: DdWorkflow,
	section: "pickup" | "drop",
): void {
	logAndAddDdAction("Use Def Address", {
		workflow,
		feature: "address_book",
		section,
	});
}

/**
 * Sends a DataDog RUM action when the user selects a saved commodity.
 */
export function ddUseSavedCommodity(workflow: DdWorkflow): void {
	logAndAddDdAction("Use Saved Commodity", {
		workflow,
	});
}

/**
 * Sends a DataDog RUM action when the user applies a saved template.
 */
export function ddUseSavedTemplate(workflow: DdWorkflow): void {
	logAndAddDdAction("Use Saved Template", {
		workflow,
	});
}

/** Records use of the compatibility path for the production Bulk Quote workbook. */
export function ddUseLegacyBulkQuoteTemplate(): void {
	logAndAddDdAction("Use Legacy Bulk Quote Template", {
		feature: "bulk_upload",
		template_type: "quote",
	});
}

/**
 * Sends a DataDog RUM action when the user uses intelligent classing.
 */
export function ddUseIntClassing(workflow: DdWorkflow): void {
	logAndAddDdAction("Use Int. Classing", {
		workflow,
	});
}
